import {
  LineItem,
  LineWithSeparator,
  ProductDetails,
  TdSub,
} from 'components/Common/Table/_styles'
import {
  formatBottleSize,
  getWineColorString,
  getWineGeoIndicationString,
  getWineNationalGITypeString,
  getWineSpecialClassificationsString,
  getWineSweetnessString,
  getWineTypeString,
  joinValues,
} from 'utils/wine'
import PackageTypeBadge from 'components/Common/PackageTypeBadge'
import Badge from 'components/Common/MasterComponents/Badge'
import { Brand } from 'api/schemas'
import { isAlcoholNull } from 'utils/validators'
import VerifiedIcon from 'components/Common/Icons/Verified'
import SuccessIcon from '../../../Pages/DataBase/Common/_assets/success.svg'
import Score from 'components/Common/Score'
import Popover from 'components/Common/Tooltips/Popover'
import ProductPopoverContent from 'components/Common/ProductPopoverContent'
import { getScaledImage } from 'utils/screen'
import { IProps } from './_methods/types'
import {
  ScoreWrapper,
  TdLink,
  TdProduct,
  TdTradeName,
  TnWrapper,
  Highlight,
  Tn,
  LastLine,
  ProductDetailsWrapper,
} from './_styles'

const WineDataColumn = ({
  product,
  isAlcoholAbv,
  hideAlcohol,
  hideBottleSize,
  hideTradeName,
  noLinks,
  verified,
  badgeSize = 12,
  showMainSynonymBadge = false,
  synonymIcon = SuccessIcon,
  showRating = false,
  noFirstLink = false,
  showPopover = false,
  highlight = false,
  fontMode = 'S',
  productLink,
  tradeNameTestId,
  productTestId,
  lineItemTestIds,
}: IProps) => {
  const wine = product?.wine
  const country = wine?.country?.localizedName
  const wineGI = joinValues(
    [getWineGeoIndicationString(wine, ' '), getWineNationalGITypeString(wine)],
    ' '
  )
  const isProductType = ['Product', 'ProductDraft', 'ProductSnapshot'].includes(
    product?.__typename || ''
  )
  const wineType = getWineTypeString(wine)
  const color = getWineColorString(wine)
  const sweetness = getWineSweetnessString(wine)
  const bottleSize = formatBottleSize(product?.bottleSize?.volumeML)
  const brandProduct =
    ['MentionWineBrand', 'Brand', 'BrandDraft'].includes(
      wine?.brand?.__typename || ''
    ) && (wine?.brand as Brand)?.name
  const brandElabelProduct =
    wine?.brand?.__typename === 'BrandELabel' && wine?.brand?.brand
  const abv = wine?.chemicalAnalysis?.alcoholVolume
  const alcoholOnLabelMin = isProductType ? product?.alcoholOnLabelMin : ''
  const alcoholOnLabelMax = isProductType ? product?.alcoholOnLabelMax : ''
  const rating = wine?.rating ?? 0
  const bottleImage =
    getScaledImage(product?.bottleImage) ||
    getScaledImage(product?.frontLabelImage) ||
    getScaledImage(product?.backLabelImage) ||
    ''
  const vintageText = wine?.vintageText || wine?.vintageTextForDraft

  const alcoholVolume =
    !isAlcoholNull(alcoholOnLabelMin) && !isAlcoholNull(alcoholOnLabelMax)
      ? alcoholOnLabelMin === alcoholOnLabelMax
        ? `${alcoholOnLabelMin}%`
        : `${alcoholOnLabelMin}-${alcoholOnLabelMax}%`
      : alcoholOnLabelMin
      ? `${alcoholOnLabelMin}%`
      : alcoholOnLabelMax
      ? `${alcoholOnLabelMax}%`
      : null
  const certs = wine?.certs || wine?.wineCerts || []
  const uniqCerts = [
    ...new Set(
      certs
        .flatMap(item =>
          item.certificationTypes?.map(cert => cert.localizedShortName)
        )
        .filter(Boolean)
    ),
  ]

  const link1 =
    !noFirstLink &&
    ['TradeName', 'MentionWineTradeName'].includes(
      wine?.tradeName?.__typename || ''
    ) &&
    wine?.tradeName?.id
      ? `/acc/producer/${wine?.tradeName?.id}`
      : ''
  const link2 =
    productLink ||
    (product?.__typename === 'ProductELabel' &&
      `/acc/product/${product?.originProduct?.slug}`) ||
    (product?.slug && `/acc/product/${product?.slug}`) ||
    (product?.wine?.id && `/acc/wine/${product?.wine.id}`)

  const line1 = (
    <TnWrapper>
      {wine?.tradeName?.mainSynonym && showMainSynonymBadge && (
        <img src={synonymIcon} alt={wine?.tradeName?.mainSynonym?.tradeName} />
      )}
      <Tn>{wine?.tradeName?.tradeName}</Tn>
    </TnWrapper>
  )

  const productDetails1Parts = [
    brandProduct || brandElabelProduct,
    wine?.keyWords,
    getWineSpecialClassificationsString(wine, ' '),
  ].filter(Boolean)

  const productDetails1 = (
    <>
      {productDetails1Parts.join(' ')}
      {vintageText !== undefined && (
        <>
          {productDetails1Parts.length > 0 && ' '}
          {highlight ? <Highlight>{vintageText}</Highlight> : vintageText}
        </>
      )}
    </>
  )

  const productDetails2 = (
    <>
      <LineItem data-testid={lineItemTestIds?.type}>{wineType}</LineItem>
      <LineItem data-testid={lineItemTestIds?.color}>{color}</LineItem>
      <LineItem data-testid={lineItemTestIds?.sugar}>{sweetness}</LineItem>
      {!hideAlcohol && isAlcoholAbv ? (
        <LineItem>{!isAlcoholNull(abv) && `${abv}%`}</LineItem>
      ) : !hideAlcohol ? (
        <LineItem>{alcoholVolume}</LineItem>
      ) : (
        ''
      )}

      {!hideBottleSize && bottleSize && (
        <LineItem data-testid={lineItemTestIds?.volume}>
          {highlight ? <Highlight>{bottleSize}</Highlight> : bottleSize}
          <PackageTypeBadge size={badgeSize} value={product?.packageType} />
        </LineItem>
      )}

      {uniqCerts.map(cert => (
        <LineItem key={cert}>
          <Badge $size={badgeSize} $emphasis='subtle' $status='positive'>
            {cert}
          </Badge>
        </LineItem>
      ))}
      {showRating && rating > 0 && (
        <LineItem>
          {
            <ScoreWrapper>
              <Score
                value={rating}
                width={12}
                height={12}
                color={'var(--color-sw-60)'}
                gap={2}
              />
            </ScoreWrapper>
          }
        </LineItem>
      )}
    </>
  )

  const productDetails3 = (
    <LineWithSeparator $separator=', '>
      <LineItem data-testid={lineItemTestIds?.country}>{country}</LineItem>
      <LineItem data-testid={lineItemTestIds?.region}>{wineGI}</LineItem>
    </LineWithSeparator>
  )

  const ProductDetailsComponent = () => (
    <ProductDetailsWrapper fontMode={fontMode}>
      {showRating ? (
        <ProductDetails $separator={' • '}>{productDetails2}</ProductDetails>
      ) : (
        <LineWithSeparator $separator={' • '}>
          {productDetails2}
        </LineWithSeparator>
      )}
    </ProductDetailsWrapper>
  )

  const productDetails1Component = (
    <TdProduct
      noLinks={noLinks || !link2}
      fontMode={fontMode}
      data-testid={productTestId}
    >
      {noLinks || !link2 ? (
        <>{productDetails1}</>
      ) : (
        <TdLink to={link2} target='_blank'>
          {productDetails1}
        </TdLink>
      )}
      {verified && (
        <div>
          <VerifiedIcon size={16} color='var(--color-velvet)' />
        </div>
      )}
    </TdProduct>
  )
  return (
    <>
      {!hideTradeName && (
        <TdTradeName
          noLinks={noLinks || !link1}
          fontMode={fontMode}
          data-testid={tradeNameTestId}
        >
          {noLinks ? (
            line1
          ) : link1 ? (
            <TdLink to={link1} target='_blank'>
              {line1}
            </TdLink>
          ) : (
            line1
          )}
        </TdTradeName>
      )}
      {showPopover ? (
        <Popover
          mode='hover'
          placement='right'
          content={() => (
            <ProductPopoverContent imageUrl={bottleImage} product={product} />
          )}
        >
          {productDetails1Component}
        </Popover>
      ) : (
        <>{productDetails1Component}</>
      )}

      <TdSub>
        <ProductDetailsComponent />
      </TdSub>
      <LastLine fontMode={fontMode}>{productDetails3}</LastLine>
    </>
  )
}

export default WineDataColumn
